import { motion } from 'framer-motion'
import { 
  Shield,
  Eye,
  Lock,
  Database,
  Globe,
  Mail,
  Calendar
} from 'lucide-react'
import MarketingNav from '@/components/marketing/MarketingNav'
import MarketingFooter from '@/components/marketing/MarketingFooter'

const lastUpdated = 'January 15, 2026'

const sections = [
  {
    id: 'introduction',
    title: 'Introduction',
    content: `Interlink Digital Group Limited t/a 3DMakerly ("3DMakerly", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-native 3D manufacturing platform, including our website, desktop application, mobile applications, and related services (collectively, the "Service").

Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.`
  },
  {
    id: 'information-collected',
    title: 'Information We Collect',
    content: `We collect information in several ways:

**Information You Provide Directly:**
- Account information (name, email address, password)
- Profile information (username, profile picture, bio)
- Payment information (processed securely through our payment providers)
- 3D models, designs, and related content you create or upload
- Communications with us (support requests, feedback)
- Survey responses and contest entries

**Information Collected Automatically:**
- Device information (device type, operating system, unique identifiers)
- Log data (IP address, browser type, pages visited, time spent)
- Usage data (features used, actions taken, preferences)
- Print telemetry data (with your consent, print outcomes, settings, timing)
- Camera data for print monitoring (processed locally by default)

**Information from Third Parties:**
- Social login providers (if you sign in with Google, GitHub, etc.)
- Analytics providers
- Payment processors`
  },
  {
    id: 'how-we-use',
    title: 'How We Use Your Information',
    content: `We use the information we collect for various purposes:

**To Provide the Service:**
- Create and manage your account
- Process your transactions
- Enable 3D design, printing, and marketplace features
- Provide customer support

**To Improve AI Models:**
- Train and improve our AI models for better predictions and generation
- We use federated learning techniques that keep your data on your device
- Aggregated, anonymised data may be used for model improvement
- You can opt out of AI training data contribution in settings

**To Communicate:**
- Send service-related notifications
- Respond to your inquiries
- Send marketing communications (with your consent)

**For Safety and Security:**
- Detect and prevent fraud
- Enforce our terms of service
- Protect users and the platform

**For Analytics:**
- Understand how users interact with our Service
- Improve features and user experience
- Conduct research and analysis`
  },
  {
    id: 'ai-data',
    title: 'AI and Your Data',
    content: `Our AI-native platform uses machine learning to provide features like print prediction, text-to-3D generation, and design assistance. Here's how we handle your data in relation to AI:

**Federated Learning:**
We primarily use federated learning, which means AI models are improved using insights from your data without the raw data ever leaving your device. Only encrypted model updates are shared, never your actual designs or prints.

**On-Device Processing:**
Many AI features run locally on your device, including:
- Print monitoring and failure detection
- Basic design suggestions
- Embedding generation for search

**Cloud Processing:**
Some features require cloud processing:
- Text-to-3D and Image-to-3D generation
- Complex generative design operations
- Semantic search across the marketplace

For cloud processing, your data is encrypted in transit and at rest, processed only for the requested operation, and not retained longer than necessary.

**Opt-Out:**
You can opt out of contributing to AI model improvement in your account settings. This does not affect your ability to use AI features.`
  },
  {
    id: 'data-sharing',
    title: 'How We Share Information',
    content: `We do not sell your personal information. We may share information in the following circumstances:

**Service Providers:**
We work with third-party companies to provide services such as:
- Cloud hosting (AWS, Cloudflare)
- Payment processing (Stripe)
- Analytics (privacy-focused providers)
- Customer support tools

**Marketplace Transactions:**
When you buy or sell on MakerHub, certain information is shared with the other party to complete the transaction.

**Legal Requirements:**
We may disclose information if required by law or in response to valid legal requests.

**Business Transfers:**
In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.

**With Your Consent:**
We may share information for other purposes with your explicit consent.`
  },
  {
    id: 'data-retention',
    title: 'Data Retention',
    content: `We retain your information for as long as your account is active or as needed to provide you services. We also retain information as necessary to:

- Comply with legal obligations
- Resolve disputes
- Enforce our agreements

**Deletion:**
You can request deletion of your account and associated data at any time through your account settings or by contacting us. Some information may be retained for legal or legitimate business purposes.

**Print Telemetry:**
Print monitoring data is retained for 90 days by default. You can adjust this in your privacy settings or delete it at any time.`
  },
  {
    id: 'your-rights',
    title: 'Your Rights and Choices',
    content: `Depending on your location, you may have certain rights regarding your personal information:

**Access:** Request a copy of your personal information
**Correction:** Request correction of inaccurate information
**Deletion:** Request deletion of your information
**Portability:** Request your data in a portable format
**Objection:** Object to certain processing of your information
**Restriction:** Request restriction of processing

**For EU/UK Users (GDPR):**
You have additional rights under GDPR, including the right to lodge a complaint with a supervisory authority.

**For California Users (CCPA):**
You have additional rights under CCPA, including the right to know what information is collected and the right to opt-out of sale of personal information (note: we do not sell personal information).

To exercise any of these rights, contact us at privacy@3dmakerly.com.`
  },
  {
    id: 'security',
    title: 'Security',
    content: `We implement appropriate technical and organisational measures to protect your information, including:

- Encryption in transit (TLS 1.3) and at rest (AES-256)
- Regular security audits and penetration testing
- Access controls and authentication
- Employee security training
- Incident response procedures

While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure.`
  },
  {
    id: 'international',
    title: 'International Data Transfers',
    content: `Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers, including:

- Standard Contractual Clauses (SCCs) for EU data
- Data Processing Agreements with service providers
- Compliance with applicable data protection laws`
  },
  {
    id: 'children',
    title: 'Children\'s Privacy',
    content: `Our Service is not intended for children under 16. We do not knowingly collect personal information from children under 16. If we learn we have collected information from a child under 16, we will delete it promptly.`
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.`
  },
  {
    id: 'contact',
    title: 'Contact Us',
    content: `If you have questions about this Privacy Policy or our privacy practices, please contact us:

**Email:** privacy@3dmakerly.com

**Data Protection Officer:**
Interlink Digital Group Limited t/a 3DMakerly
Attn: Data Protection Officer
27 Old Gloucester Street
London, WC1N 3AX
United Kingdom

Company: 05431902 | VAT: GB132978001`
  },
]

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-surface-950">
      <MarketingNav />
      
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-20" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-8"
          >
            <Shield className="w-4 h-4 text-primary-400" />
            <span className="text-sm font-medium text-primary-300">Legal</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Privacy Policy
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-4 text-surface-400"
          >
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Last updated: {lastUpdated}
            </span>
          </motion.div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-8 border-y border-surface-800 bg-surface-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success-500/10">
                <Lock className="w-5 h-5 text-success-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">End-to-end encrypted</p>
                <p className="text-xs text-surface-500">Your data is protected</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success-500/10">
                <Eye className="w-5 h-5 text-success-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">No data selling</p>
                <p className="text-xs text-surface-500">We never sell your data</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success-500/10">
                <Database className="w-5 h-5 text-success-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Federated learning</p>
                <p className="text-xs text-surface-500">AI learns locally</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success-500/10">
                <Globe className="w-5 h-5 text-success-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">GDPR compliant</p>
                <p className="text-xs text-surface-500">Global privacy standards</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 border-b border-surface-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-lg font-semibold text-white mb-4">Contents</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {sections.map((section, index) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-sm text-surface-400 hover:text-primary-400 transition-colors"
              >
                {index + 1}. {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert prose-surface max-w-none">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mb-12 scroll-mt-32"
              >
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="text-surface-500">{index + 1}.</span>
                  {section.title}
                </h2>
                <div className="text-surface-300 leading-relaxed whitespace-pre-line">
                  {section.content.split('\n').map((paragraph, i) => {
                    if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return (
                        <h3 key={i} className="text-lg font-semibold text-white mt-6 mb-2">
                          {paragraph.replace(/\*\*/g, '')}
                        </h3>
                      )
                    }
                    if (paragraph.startsWith('- ')) {
                      return (
                        <li key={i} className="ml-4 text-surface-400">
                          {paragraph.substring(2)}
                        </li>
                      )
                    }
                    if (paragraph.trim() === '') return <br key={i} />
                    return <p key={i} className="mb-4">{paragraph}</p>
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 bg-surface-900/30 border-t border-surface-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-500/10 mb-4">
            <Mail className="w-6 h-6 text-primary-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Questions about privacy?</h2>
          <p className="text-surface-400 mb-4">
            Contact our Data Protection Officer at{' '}
            <a href="mailto:privacy@3dmakerly.com" className="text-primary-400 hover:text-primary-300 transition-colors">
              privacy@3dmakerly.com
            </a>
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
